import type { PopupOptions } from '@typeform/embed/types/factories/create-popup/popup-options';

type TypeformPopup = {
	open: () => void;
	close: () => void;
};

// Cache for the typeform module
let typeformModule: typeof import('@typeform/embed') | null = null;

/**
 * Lazy-loading composable for Typeform popups
 * Only loads the @typeform/embed library when openTypeform() is called
 */
export function useTypeformPopup(typeformId: string, options?: PopupOptions) {
	const popup = ref<TypeformPopup>();
	const isLoading = ref(false);

	/**
	 * Load Typeform library dynamically
	 */
	async function loadTypeform() {
		if (typeformModule) return typeformModule;

		// Dynamic import - only loads when needed
		typeformModule = await import('@typeform/embed');
		return typeformModule;
	}

	/**
	 * Open the Typeform popup
	 * Lazily loads the library on first call
	 */
	const openTypeform = async () => {
		if (isLoading.value) return;

		isLoading.value = true;

		try {
			const typeform = await loadTypeform();

			const container = document.createElement('div');
			container.style.pointerEvents = 'auto';
			document.body.appendChild(container);

			popup.value = typeform.createPopup(typeformId, {
				...options,
				container,
				onClose: () => container.remove()
			});

			popup.value.open();
		} catch (error) {
			console.error('Failed to load Typeform:', error);
		} finally {
			isLoading.value = false;
		}
	};

	onUnmounted(() => popup.value?.close());

	return {
		openTypeform,
		isLoading: readonly(isLoading)
	};
}
